import {
  faCheckCircle,
  faFileLines,
  faFolder,
  faHomeAlt,
  faSignal,
  faUser,
  faUserFriends,
  faUserGear,
  faClock,
  faGear,
  faMemoCircleCheck,
} from "@fortawesome/pro-light-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import React, { Dispatch, SetStateAction, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";

import { useLocation } from "react-router-dom";
import { IMAGES } from "src/assets";
import { useIsMobile } from "src/hooks/useIsMobile";
import { setOpenedAssetsPage, setOpenedClientsPage } from "src/redux/actions/openedPages";
import { getUserData } from "src/redux/selectors/user";
import { colors } from "src/styles";
import { testGroup } from "src/utils/helpers";
import { NavbarItem } from "./NavbarItem";
import { NavbarToggle } from "./NavbarToggle";
import { ToggleArrow } from "./ToggleArrow";

interface NavbarProps {
  isVisible: boolean;
  setIsVisible: Dispatch<SetStateAction<boolean>>;
}

interface NavItemConfig {
  title: string;
  href: string;
  icon: IconDefinition;
  isActive: boolean;
  onClick?: () => void;
}

interface NavSectionConfig {
  label: string;
  items: NavItemConfig[];
}

const Navbar: React.FC<NavbarProps> = ({ isVisible, setIsVisible }) => {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const { isMobile } = useIsMobile();

  const { isManager, email, isClient, clientId } = useSelector(getUserData);

  const isTestGroup = testGroup.includes(email ?? "");

  const resetClientsPage = () => dispatch(setOpenedClientsPage(1));
  const resetAssetsPage = () => dispatch(setOpenedAssetsPage(1));

  const sections: NavSectionConfig[] = [
    {
      label: "Workspace",
      items: [
        {
          title: "Home",
          href: "/",
          isActive: pathname === "/",
          icon: faHomeAlt,
          onClick: resetClientsPage,
        },
        ...(clientId !== "OXFORDCOUNTY"
          ? [
              {
                title: "Operational Awareness",
                href: "/operational-awareness",
                isActive: pathname.includes("/operational-awareness") || pathname.includes("/workrequests/overview"),
                icon: faSignal,
                onClick: resetClientsPage,
              },
              {
                title: "Work Requests",
                href: "/workrequests",
                isActive: pathname.includes("/workrequests"),
                icon: faCheckCircle,
                onClick: resetClientsPage,
              },
              {
                title: "Preventative Maintenance",
                href: "/preventative-maintenance",
                isActive: pathname.includes("/preventative-maintenance"),
                icon: faMemoCircleCheck,
                onClick: resetClientsPage,
              },
            ]
          : []),
        ...(isTestGroup
          ? [
              {
                title: "Files",
                href: "/files",
                isActive: pathname.includes("/files"),
                icon: faFolder,
                onClick: resetClientsPage,
              },
            ]
          : []),
      ],
    },
    {
      label: "Resources",
      items: [
        ...(isManager
          ? [
              {
                title: "Clients",
                href: "/clients",
                isActive: pathname.includes("/clients"),
                icon: faUserFriends,
                onClick: resetClientsPage,
              },
              {
                title: "Team Members",
                href: "/team-members",
                isActive: pathname.includes("/team-members"),
                icon: faUser,
                onClick: resetClientsPage,
              },
              {
                title: "Vendors",
                href: "/vendors",
                isActive: pathname.includes("/vendors"),
                icon: faUserGear,
                onClick: resetClientsPage,
              },
            ]
          : []),
        ...(isManager || isTestGroup
          ? [
              {
                title: "Assets",
                href: "/assets",
                isActive: pathname.includes("/assets"),
                icon: faGear,
                onClick: resetAssetsPage,
              },
            ]
          : []),
      ],
    },
    {
      label: "Operations",
      items: [
        ...(isManager
          ? [
              {
                title: "Purchasing",
                href: "/purchasing",
                isActive: pathname.includes("/purchasing"),
                icon: faFileLines,
                onClick: resetClientsPage,
              },
              {
                title: "Forms",
                href: "/dynamic-forms",
                isActive: pathname.includes("/dynamic-forms"),
                icon: faFileLines,
                onClick: resetClientsPage,
              },
              {
                title: "Logs",
                href: "/washroomlogs",
                isActive: pathname.includes("/washroomlogs"),
                icon: faClock,
                onClick: resetClientsPage,
              },
            ]
          : []),
        ...(isClient
          ? [
              {
                title: "Logs",
                href: "/washroomlogs?user=client",
                isActive: pathname.includes("/washroomlogs") || search.includes("user=client"),
                icon: faClock,
                onClick: resetClientsPage,
              },
              ...(clientId !== "OXFORDCOUNTY"
                ? [
                    {
                      title: "Time & Attendance",
                      href: "/time-and-attendance",
                      isActive: pathname.includes("/time-and-attendance"),
                      icon: faMemoCircleCheck,
                      onClick: resetClientsPage,
                    },
                  ]
                : []),
            ]
          : []),
      ],
    },
  ].filter((section) => section.items.length > 0);

  useEffect(() => {
    setIsVisible(!isMobile);
  }, [isMobile]);

  return (
    <div style={{ width: !isMobile ? (isVisible ? "260px" : "72px") : "0px" }}>
      <Container $isMobile={isMobile} $isVisible={isVisible}>
        <NavbarToggle $isMobile={isMobile} $isVisible={isVisible} setIsVisible={setIsVisible} />

        {!isMobile && <ToggleArrow isVisible={isVisible} setIsVisible={setIsVisible} />}

        <Content $isMobile={isMobile} $isVisible={isVisible}>
          <BrandBlock $isVisible={isVisible}>
            {isVisible ? (
              <ExpandedLogo src={IMAGES.APP_LOGO} />
            ) : (
              <CollapsedLogo src={IMAGES.COLLAPSED_LOGO_NAV_HIGH_RES} />
            )}
            {isVisible && (
              <BrandCopy>
                <BrandTitle>Kleenway</BrandTitle>
                <BrandMeta>Client portal operating system</BrandMeta>
              </BrandCopy>
            )}
          </BrandBlock>

          <Sections>
            {sections.map((section) => (
              <Section key={section.label} $isVisible={isVisible}>
                {isVisible && <SectionLabel>{section.label}</SectionLabel>}
                {section.items.map((item) => (
                  <NavbarItem
                    key={item.href}
                    title={item.title}
                    href={item.href}
                    isActive={item.isActive}
                    icon={item.icon}
                    isVisible={isVisible}
                    onClick={item.onClick}
                  />
                ))}
              </Section>
            ))}
          </Sections>
        </Content>
      </Container>
    </div>
  );
};

export interface ContainerProps {
  $isVisible: boolean;
  $isMobile: boolean;
}

const Container = styled.nav<ContainerProps>`
  overflow-y: auto;

  display: flex;
  flex-direction: column;
  background-color: ${colors.white.default};
  border-right: 1px solid ${colors.grey.light3};
  width: ${({ $isVisible }) => ($isVisible ? "260px" : "72px")};
  padding: ${({ $isVisible }) => ($isVisible ? "20px 16px 24px" : "20px 10px 24px")};
  height: ${({ $isVisible, $isMobile }) => ($isMobile ? ($isVisible ? "100%" : "64px") : "100%")};
  position: fixed;
  top: 0;
  z-index: ${({ $isMobile }) => ($isMobile ? 100 : 50)};

  ${({ $isMobile }) =>
    $isMobile &&
    css`
      background-color: ${colors.white.default};
      margin-top: 0;
      padding-bottom: 0;
    `};
`;

const Content = styled.div<ContainerProps>`
  overflow-y: ${({ $isMobile }) => ($isMobile ? "auto" : "initial")};
  display: ${({ $isMobile, $isVisible }) => ($isMobile ? ($isVisible ? "block" : "none") : "block")};
  min-height: 100%;
  display: flex;
  flex-direction: column;
`;

const BrandBlock = styled.div<{ $isVisible: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ $isVisible }) => ($isVisible ? "12px" : "0")};
  padding: ${({ $isVisible }) => ($isVisible ? "8px 8px 20px" : "8px 0 20px")};
  margin-bottom: 8px;
  border-bottom: 1px solid ${colors.grey.light15};
`;

const BrandCopy = styled.div`
  min-width: 0;
`;

const BrandTitle = styled.p`
  margin: 0;
  color: ${colors.black.dark1};
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.02em;
`;

const BrandMeta = styled.p`
  margin: 2px 0 0;
  color: ${colors.grey.dark6};
  font-size: 11px;
  line-height: 1.4;
`;

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  flex: 1;
`;

const Section = styled.section<{ $isVisible: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0 ${({ $isVisible }) => ($isVisible ? "-4px" : "0")};
`;

const SectionLabel = styled.p`
  margin: 0 8px 6px;
  color: ${colors.grey.dark6};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

const ExpandedLogo = styled.img`
  width: 42px;
  height: 42px;
  object-fit: contain;
`;

const CollapsedLogo = styled.img`
  width: 32px;
  height: 32px;
  margin: 0 auto;
  display: flex;
`;

export default Navbar;
